import Icon from "@ant-design/icons";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  ConfigProvider,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Result,
  Spin,
  Tooltip,
  Tour,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BranchSelector from "../components/BranchSelector";
import { useAuth } from "../context/AuthContext";
import useCameras from "../hooks/useCameras";
import useZones from "../hooks/useZones";
import { MENU_ITEMS_DESCRIPTION } from "../utils/constants";
import useApi from "../utils/useApi";

const { Header, Content, Sider } = Layout;

function getIcon(src, disable = false) {
  return (
    <Icon
      component={() => (
        <img
          src={src}
          style={{ width: "20px", height: "20px", opacity: disable ? 0.5 : 1 }}
          alt=""
        />
      )}
    />
  );
}

function getItem(label, key, icon, children, type, disabled) {
  return {
    key,
    icon,
    children,
    label,
    type,
    disabled,
  };
}

function MenuBar({ children, menu_item_selected }) {
  const [collapsed, setCollapsed] = useState(false);
  const [siderHeight, setSiderHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [contentMargin, setContentMargin] = useState(0);
  const tutorialRef1 = useRef(null);
  const {
    user,
    logout,
    shouldShowTutorialBool,
    isZeroPhaseTutorialOpen,
    setIsZeroPhaseTutorialOpen,
  } = useAuth();
  const isAdmin = user?.id_admin || user?.is_admin;
  const navigate = useNavigate();
  const location = useLocation();

  const { camerasCount, isLoading: isLoadingCameras } = useCameras();
  const { data, isLoading: isLoadingZones } = useZones({
    active_only: true,
  });

  const zonesCount = data?.length;

  const hasCameras = camerasCount && camerasCount > 0;
  const hasZones = zonesCount && zonesCount > 0;

  const shouldShow =
    !isLoadingCameras && !isLoadingZones && hasCameras && hasZones;

  const onClick = (e) => {
    if (e.key === "home") {
      navigate("/");
    } else if (e.key === "g3_sub1") {
      window.open("https://www.montini.tech/");
    } else if (e.key === "g3_sub2") {
      return;
    } else {
      navigate("/" + e.key);
    }
  };

  const steps = [
    {
      title: "1º passo: Abrir menu de câmeras e zonas",
      description:
        "No menu de cameras e zonas é possível adiciionar câmeras e posteriormente criar zonas.",
      nextButtonProps: {
        children: "Próximo",
        onClick: () => navigate("/zones_manager"),
      },
      target: () => tutorialRef1.current,
    },
  ];
  const { getTestConnection } = useApi();
  const {
    isSuccess: isTestConnectionSuccess,
    isLoading: isTestConnectionLoading,
  } = useQuery({
    queryKey: ["test_connection"],
    queryFn: () => getTestConnection(),
  });

  useEffect(() => {
    const HEADER_HEIGHT = 64;
    const CONTENT_MARGIN_PADDING = 14;
    const COLLAPSE_BREAKPOINT = 1500;

    const handleResize = () => {
      setHeaderHeight(HEADER_HEIGHT);
      setContentMargin(HEADER_HEIGHT + CONTENT_MARGIN_PADDING);
      setSiderHeight(window.innerHeight - HEADER_HEIGHT);

      if (window.innerWidth <= COLLAPSE_BREAKPOINT) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const items = [
    getItem("Home", "home", getIcon("icons/home.png"), null, "item", false),
    { type: "divider" },
    getItem(
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        Faturamento
        <Tooltip title={MENU_ITEMS_DESCRIPTION["revenue"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "revenue",
      getIcon("icons/money.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    { type: "divider" },
    getItem(
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        Fluxo de pessoas
        <Tooltip title={MENU_ITEMS_DESCRIPTION["flow_detections"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "flow_detections",
      getIcon("icons/people.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        Análise de eventos
        <Tooltip title={MENU_ITEMS_DESCRIPTION["collections_change"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "collections_change",
      getIcon("icons/tshirt.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        Horários de pico
        <Tooltip title={MENU_ITEMS_DESCRIPTION["peak_hours"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "peak_hours",
      getIcon("icons/graph.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    { type: "divider" },
    getItem(
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        Gerenciar eventos
        <Tooltip title={MENU_ITEMS_DESCRIPTION["events_manager"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "events_manager",
      getIcon("icons/pencil.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      <div
        ref={tutorialRef1}
        style={{ display: "flex", justifyContent: "space-between" }}>
        Editar zonas
        <Tooltip title={MENU_ITEMS_DESCRIPTION["zones_manager"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "zones_manager",
      getIcon("icons/size.png"),
      null,
      "item"
    ),
    getItem(
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        Horários da Loja
        <Tooltip title={MENU_ITEMS_DESCRIPTION["opening_hours"]}>
          <InfoCircleOutlined
            style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
          />
        </Tooltip>
      </div>,
      "opening_hours",
      getIcon("icons/clock.png"),
      null,
      "item"
    ),
    { type: "divider" },
    ...(isAdmin
      ? [
          getItem(
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              Painel de Controle
              <Tooltip title={MENU_ITEMS_DESCRIPTION["control_panel"]}>
                <InfoCircleOutlined
                  style={{ marginLeft: 10, color: "gray", fontSize: 14 }}
                />
              </Tooltip>
            </div>,
            "control_panel",
            getIcon("icons/painel.png"),
            null,
            "item"
          ),
        ]
      : []),
  ];

  const top_items = [{ label: "" }];

  const help_items = [
    {
      label: "Fale conosco",
      key: "g3_sub1",
      icon: getIcon("icons/phone.png"),
      type: "item",
    },
    {
      label: "Sair",
      key: "g3_sub2",
      icon: getIcon("icons/logout.png"),
      type: "item",
      onClick: () => logout(),
    },
  ];

  const avatarMenuItems = [
    {
      key: "logout",
      label: "Sair",
      icon: getIcon("icons/logout.png"),
      onClick: () => {
        logout();
      },
    },
  ];

  return (
    <>
      {isTestConnectionLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}>
          <Spin size="large" />
        </div>
      ) : isTestConnectionSuccess ? (
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                iconSize: "20px",
                itemMarginBlock: "15px",
                groupTitleColor: "black",
                itemSelectedBg: "#E9E9E9",
                itemSelectedColor: "black",
                horizontalItemHoverColor: null,
                horizontalItemSelectedColor: null,
              },
              Layout: {
                siderBg: "white",
                colorBgLayout: "white",
              },
            },
          }}>
          <Layout hasSider>
            <Sider
              trigger={null}
              collapsible
              collapsed={collapsed}
              style={{
                overflow: "auto",
                height: siderHeight,
                position: "fixed",
                left: 0,
                top: headerHeight,
                bottom: 0,
                zIndex: 1000,
              }}
              width={collapsed ? 80 : 230}>
              <Menu
                style={{ border: "0px", width: "100%" }}
                defaultSelectedKeys={menu_item_selected}
                mode="inline"
                onClick={onClick}
                align={collapsed ? "center" : "left"}
                items={items}
              />
              <Menu
                style={{
                  border: "0px",
                  width: "100%",
                  position: "absolute",
                  bottom: "0px",
                }}
                mode="inline"
                onClick={onClick}
                align={collapsed ? "center" : "left"}
                items={help_items}
                selectedKeys={[]}
              />
            </Sider>
            <Layout>
              <Header
                style={{
                  padding: 0,
                  position: "fixed",
                  zIndex: 1,
                  width: "100%",
                  background: "white",
                }}
                className="header">
                <Flex
                  style={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    padding: "0 27px",
                    zIndex: 0,
                  }}
                  items={top_items}>
                  <Button
                    type="text"
                    icon={getIcon("icons/menu.png")}
                    style={{
                      background: !collapsed ? "rgba(0, 0, 0, 0.20)" : null,
                    }}
                    onClick={() => {
                      setCollapsed((prev) => !prev);
                    }}
                  />
                  <Icon
                    component={() => (
                      <img
                        src={"icons/logo_empresa.svg"}
                        alt="logo"
                        style={{ width: "200px", height: headerHeight }}
                      />
                    )}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "left",
                    }}
                  />
                  <BranchSelector />
                  <Button
                    type="text"
                    icon={getIcon("icons/settings.png")}
                    onClick={() => navigate("/settings")}
                  />
                  <Dropdown
                    menu={{ items: avatarMenuItems }}
                    trigger={["click"]}
                    placement="bottom"
                    arrow={{ pointAtCenter: true }}>
                    <Avatar
                      style={{
                        marginLeft: "10px",
                        marginRight: "10px",
                        cursor: "pointer",
                      }}
                      shape="square"
                      size={{ xs: 24, sm: 32, md: 40 }}>
                      {user.first_name[0].toUpperCase() +
                        user.last_name[0].toUpperCase()}
                    </Avatar>
                  </Dropdown>
                </Flex>
              </Header>
              <Content
                style={{
                  marginTop: contentMargin,
                  marginLeft: collapsed ? 80 : 220,
                  overflow: "initial",
                  backgroundColor: "white",
                }}
                width="100%">
                {children}
              </Content>
            </Layout>
          </Layout>
        </ConfigProvider>
      ) : (
        <Result
          status="500"
          title="Erro: Não foi possível conectar"
          subTitle="O aplicativo não conseguiu se conectar ao servidor."
        />
      )}
      <Tour
        open={
          shouldShowTutorialBool &&
          location.pathname === "/" &&
          isZeroPhaseTutorialOpen
        }
        onClose={() => setIsZeroPhaseTutorialOpen(false)}
        steps={steps}
        gap={{
          offset: [45, 0],
          radius: 10,
        }}
      />
    </>
  );
}

export default MenuBar;
